import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { FileText, TrendingUp, Award, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, checkAuth } = useAuth();
  const [stats, setStats] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, resultsRes] = await Promise.all([
        axios.get(`${API}/user/stats`, { withCredentials: true }),
        axios.get(`${API}/results`, { withCredentials: true })
      ]);

      setStats(statsRes.data);
      setResults(resultsRes.data);
    } catch (error) {
      toast.error('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentSuccess = params.get('payment');
    
    // Handle PayFast return
    if (paymentSuccess === 'success') {
      toast.success('Payment successful! Your account has been upgraded.');
      checkAuth();
      navigate('/dashboard', { replace: true });
    }

    fetchDashboardData();
  }, [location, checkAuth, navigate, fetchDashboardData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const usagePercent = stats ? (stats.word_count_used / stats.words_limit) * 100 : 0;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 py-12">
        <div className="container mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-black tracking-tighter mb-2">Dashboard</h1>
            <p className="text-muted-foreground">Welcome back, {user?.name}</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="p-6 border-zinc-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold">Current Plan</h3>
                <Award className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold mb-2">{stats?.tier === 'free' ? 'Free' : stats?.tier === 'monthly' ? 'Monthly' : 'Annual'}</p>
              {stats?.tier === 'free' && (
                <Link to="/pricing">
                  <Button size="sm" className="mt-2">Upgrade</Button>
                </Link>
              )}
            </Card>

            <Card className="p-6 border-zinc-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold">Words Used</h3>
                <TrendingUp className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold font-mono mb-2">
                {stats?.word_count_used.toLocaleString()} / {stats?.words_limit.toLocaleString()}
              </p>
              <Progress value={usagePercent} className="h-2" />
            </Card>

            <Card className="p-6 border-zinc-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold">Total Results</h3>
                <FileText className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold font-mono">{stats?.result_count || 0}</p>
            </Card>
          </div>

          {/* Recent Results */}
          <Card className="p-6 border-zinc-200">
            <h2 className="text-2xl font-heading font-bold mb-6">Recent Results</h2>
            
            {results.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">No results yet</p>
                <Link to="/">
                  <Button>Humanize Your First Text</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {results.map((result) => (
                  <div
                    key={result.result_id}
                    className="border border-zinc-200 rounded-lg p-4 hover:bg-zinc-50 transition"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline">{result.style}</Badge>
                          <span className="text-xs text-muted-foreground font-mono">
                            {result.word_count} words
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(result.created_at), 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {result.input_text.substring(0, 150)}...
                        </p>
                      </div>
                      <Link to={`/r/${result.result_id}`} target="_blank">
                        <Button size="sm" variant="ghost">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;
